import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Tooltip, IconButton } from '@mui/material';
import { useGridApiRef } from '@mui/x-data-grid-pro';
import ControlledDataGrid from '../../components/DataGrid/ControlledDataGrid';
import EditIcon from '@mui/icons-material/Edit';
import moment from 'moment';
import axios from 'axios';
import { APIURL } from '../../config';
import ReferralDetailsModal from './ReferralDetailsModal';

const VALID_STATUSES = [
  { value: 'REFERRAL_INITIATED', label: 'Referral Initiated' },
  { value: 'INITIAL_CONTACT', label: 'Initial Contact' },
  { value: 'DOCUMENTATION_PENDING', label: 'Documentation Pending' },
  { value: 'DOCUMENTATION_RECEIVED', label: 'Documentation Received' },
  { value: 'DETERMINATION_PENDING', label: 'Determination Pending' },
  { value: 'ACCEPTED', label: 'Accepted' },
  { value: 'DENIED', label: 'Denied' },
  { value: 'ON_HOLD', label: 'On Hold' },
  { value: 'INTAKE_IN_PROGRESS', label: 'Intake in Progress' },
  { value: 'CLOSED', label: 'Closed' },
];

const formatStatus = (status) => {
  const found = VALID_STATUSES.find(s => s.value === status);
  return found ? found.label : status;
};

const isActiveStatus = (status) => {
  if (!status) return true; // treat undefined as active
  const s = String(status).toUpperCase();
  return ['ACTIVE','OPEN','PENDING','IN_PROGRESS'].includes(s);
};

const ReferralGrid = ({ comp_id, user }) => {
  const apiRef = useGridApiRef();
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [selectedReferral, setSelectedReferral] = useState(null);

  const fetchRows = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${APIURL}/referral`, { params: { comp_id } });
      const arr = Array.isArray(res.data) ? res.data : (res.data ? [res.data] : []);
      const roleOf = (r) => r.company_role || (r.initiator_com_id === comp_id ? 'initiated' : (r.receiver_com_id === comp_id ? 'receiving' : ''));
      const mapped = arr.map((r, index) => {
        const role = roleOf(r);
        const baseTitle = r.referral_subject || r.subject || r.form_name || 'Referral';
        const status = r.referral_status || r.status || 'PENDING';
        const date = r.referral_date || r.created_at;
        console.log(date);
        
        const initiatorName = r.initiator_com_name
          || r.initiator_name
          || (r.initiator_com_id ? `Company #${r.initiator_com_id}` : '—');

        return {
          id: r.referral_id || index,
          referral_id: r.referral_id,
          initiator: initiatorName,
          subject: baseTitle,
          direction: role === 'initiated' ? 'Outgoing' : (role === 'receiving' ? 'Incoming' : ''),
          status: String(status).toUpperCase(),
          createdAt: date,
          raw: r,
        };
      });
      setRows(mapped);
    } catch (e) {
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [comp_id]);

  useEffect(() => { fetchRows(); }, [fetchRows]);

  const handleOpenDetails = (row) => {
    setSelectedReferral(row.raw);
    setDetailsOpen(true);
  };

  const columns = useMemo(() => ([
    {
      field: 'details',
      headerName: 'Details',
      width: 80,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      pinnable: true,
      renderCell: (params) => (
        <Tooltip title="View Details">
          <IconButton color="primary" size="small" onClick={() => handleOpenDetails(params.row)}>
            <EditIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      ),
    },
    { field: 'initiator', headerName: 'Initiator', width: 200 },
    { field: 'subject', headerName: 'Subject', width: 200, flex: 1 },
    { field: 'direction', headerName: 'Direction', width: 110 },
    { field: 'status', headerName: 'Status', width: 160, valueFormatter: (value) => formatStatus(value) },
    {
      field: 'createdAt',
      headerName: 'Created At',
      width: 160,
      type: 'date',
      valueFormatter: (value) => (value ? moment(value).format('MM/DD/YYYY') : ''),
    },
    { field: 'referral_id', headerName: 'Referral ID', width: 120 },
  ]), [handleOpenDetails]);

  return (
    <>
        <Box sx={{ minHeight: 0, height: 'calc(100vh - 150px)', backgroundColor: '#f5f5f5' }}>
          <ControlledDataGrid
            gridId={75}
            user={user}
            apiRef={apiRef}
            initialState={{
              sorting: { sortModel: [{ field: 'createdAt', sort: 'desc' }] },
              pinnedColumns: { left: ['details'] },
              columns: { columnVisibilityModel: { referral_id: false } },
            }}
            getRowId={(row) => row.id}
            rows={rows}
            columns={columns}
            disableRowSelectionOnClick
            onRowClick={(params) => handleOpenDetails(params.row)}
            sx={{
              backgroundColor: '#fff',
              flex: 1,
              height: '100%',
              '& .MuiDataGrid-columnHeaderTitle': {
                whiteSpace: 'normal',
                lineHeight: 1.2,
                textAlign: 'left',
                wordBreak: 'break-word',
                maxWidth: '100%',
              },
            }}
            loading={loading}
          />
        </Box>
      <ReferralDetailsModal
        open={detailsOpen}
        onClose={() => setDetailsOpen(false)}
        referral={selectedReferral}
        comp_id={comp_id}
        apiRef={apiRef}
        user={user}
      />
    </>
  );
};

export default ReferralGrid;
